import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { assessmentsAPI, checklistAPI, organisationsAPI, evidenceAPI } from '../services/api';
import { ChecklistCategory, ChecklistQuestion, Assessment, Organisation, Evidence } from '../types';
import { useAuth } from '../context/AuthContext';

type Step = 'select' | 'category' | 'review' | 'submitted';
type CatTab = 'questions' | 'evidence';

const ALLOWED_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx'];
const MAX_MB = 10;

interface QuestionResponse {
  question_id: number;
  response_value: string | null;
  is_applicable: boolean;
  comments: string;
}

const CATEGORIES_ORDER = [
  'Policy & Documentation',
  'ICC Formation & Composition',
  'Awareness & Training',
  'Grievance & Case Management',
  'Reporting & Annual Returns',
];

const AssessmentPage: React.FC = () => {
  const { organisationId } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('select');
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [activeAssessment, setActiveAssessment] = useState<Assessment | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const [categories, setCategories] = useState<ChecklistCategory[]>([]);
  const [questions, setQuestions] = useState<ChecklistQuestion[]>([]);
  const [currentCatIndex, setCurrentCatIndex] = useState(0);
  const [responses, setResponses] = useState<Record<number, QuestionResponse>>({});
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  // Evidence tab state
  const [catTab, setCatTab] = useState<CatTab>('questions');
  const [catEvidence, setCatEvidence] = useState<Evidence[]>([]);
  const [evidenceLoading, setEvidenceLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  // Org taxonomy — used to filter questions to those relevant for this org
  const orgProfileRef = useRef<Organisation | null>(null);

  useEffect(() => {
    const load = async () => {
      const promises: [Promise<any>, Promise<any>, Promise<any>?] = [
        assessmentsAPI.list(),
        checklistAPI.categories(),
        organisationId ? organisationsAPI.get(organisationId) : Promise.resolve(null),
      ];
      const [aRes, cRes, orgRes] = await Promise.all(promises);
      if (orgRes?.data) orgProfileRef.current = orgRes.data as Organisation;
      setAssessments(aRes.data);
      const sorted = (cRes.data as ChecklistCategory[]).sort(
        (a, b) => a.order_index - b.order_index
      );
      setCategories(sorted);
      setLoading(false);
    };
    load();
  }, [organisationId]);

  const loadCategoryQuestions = async (catIdx: number) => {
    if (!categories[catIdx]) return;
    const org = orgProfileRef.current;
    const params: Record<string, any> = { category_id: categories[catIdx].id };
    if (org?.industry) params.industry = org.industry;
    if (org?.sub_industry) params.sub_industry = org.sub_industry;
    if (org?.unit_type) params.unit_type = org.unit_type;
    if (org?.workforce_types?.length) params.workforce_type = org.workforce_types[0];
    if (org?.work_modes?.length) params.work_mode = org.work_modes[0];
    const res = await checklistAPI.questions(params);
    const qs = (res.data as ChecklistQuestion[]).sort((a, b) => a.order_index - b.order_index);
    setQuestions(qs);
    setCurrentCatIndex(catIdx);
    // init responses
    const init: Record<number, QuestionResponse> = { ...responses };
    qs.forEach((q) => {
      if (!init[q.id]) {
        init[q.id] = { question_id: q.id, response_value: null, is_applicable: true, comments: '' };
      }
    });
    setResponses(init);
  };

  const loadCategoryEvidence = async (assessmentId: number, catId: number) => {
    setEvidenceLoading(true);
    try {
      const res = await evidenceAPI.list(assessmentId, catId);
      setCatEvidence(res.data);
    } catch {
      setCatEvidence([]);
    } finally {
      setEvidenceLoading(false);
    }
  };

  const handleEvidenceUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!activeAssessment || !e.target.files?.[0]) return;
    const cat = categories[currentCatIndex];
    const file = e.target.files[0];
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      setUploadError(`File type .${ext} not allowed. Permitted: ${ALLOWED_EXTENSIONS.join(', ')}`);
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      setUploadError(`File too large. Max: ${MAX_MB} MB`);
      return;
    }
    setUploadError(null);
    setUploading(true);
    const form = new FormData();
    form.append('assessment_id', String(activeAssessment.id));
    form.append('category_id', String(cat.id));
    form.append('file', file);
    try {
      await evidenceAPI.upload(form);
      setUploadSuccess('File uploaded successfully');
      await loadCategoryEvidence(activeAssessment.id, cat.id);
      setTimeout(() => setUploadSuccess(null), 3000);
    } catch (err: any) {
      setUploadError(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleEvidenceDelete = async (evidenceId: number) => {
    if (!activeAssessment || !window.confirm('Delete this evidence file?')) return;
    const cat = categories[currentCatIndex];
    await evidenceAPI.delete(evidenceId);
    await loadCategoryEvidence(activeAssessment.id, cat.id);
  };

  const startNewAssessment = async () => {
    setStartError(null);
    if (!organisationId) {
      setStartError('Your account is not linked to an organisation. Ask a Super Admin to assign you to one before starting an assessment.');
      return;
    }
    try {
      const res = await assessmentsAPI.create({ organisation_id: organisationId });
      setActiveAssessment(res.data);
      await loadCategoryQuestions(0);
      setStep('category');
    } catch (e: any) {
      setStartError(e?.response?.data?.detail || 'Failed to create assessment. Please try again.');
    }
  };

  const resumeAssessment = async (a: Assessment) => {
    setActiveAssessment(a);
    await loadCategoryQuestions(0);
    setCatTab('questions');
    setCatEvidence([]);
    setStep('category');
  };

  const handleResponse = (qid: number, field: keyof QuestionResponse, val: any) => {
    setResponses((prev) => ({ ...prev, [qid]: { ...prev[qid], [field]: val } }));
  };

  const saveAndNext = async () => {
    if (!activeAssessment) return;
    setSaving(true);
    const payload = questions.map((q) => responses[q.id]).filter(Boolean);
    await assessmentsAPI.saveResponses(activeAssessment.id, { responses: payload });
    setSaving(false);
    setCatTab('questions');
    setCatEvidence([]);

    if (currentCatIndex + 1 < categories.length) {
      await loadCategoryQuestions(currentCatIndex + 1);
    } else {
      setStep('review');
    }
  };

  const handleSubmit = async () => {
    if (!activeAssessment) return;
    setSubmitting(true);
    await assessmentsAPI.submit(activeAssessment.id);
    setSubmitting(false);
    setStep('submitted');
  };

  if (loading) {
    return <div className="flex justify-center py-16"><div className="animate-spin h-10 w-10 border-b-2 border-blue-600 rounded-full" /></div>;
  }

  // ── Select screen ─────────────────────────────────────────────────────────
  if (step === 'select') {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">POSH Assessment</h1>
        <button
          onClick={startNewAssessment}
          className="w-full py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          + Start New Assessment
        </button>
        {startError && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            ⚠️ {startError}
          </div>
        )}
        {assessments.filter(a => a.status === 'draft').length > 0 && (
          <div>
            <h2 className="text-sm font-semibold text-gray-600 mb-2">Resume Draft</h2>
            {assessments.filter(a => a.status === 'draft').map(a => (
              <button
                key={a.id}
                onClick={() => resumeAssessment(a)}
                className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:border-blue-400 bg-white mb-2 text-sm"
              >
                <span className="font-medium">Assessment #{a.id}</span>
                <span className="ml-2 text-gray-500">{new Date(a.created_at).toLocaleDateString()}</span>
                <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs">Draft</span>
              </button>
            ))}
          </div>
        )}
        {assessments.filter(a => a.status !== 'draft').length > 0 && (
          <div>
            <h2 className="text-sm font-semibold text-gray-600 mb-2">Completed Assessments</h2>
            {assessments.filter(a => a.status !== 'draft').map(a => (
              <div key={a.id} className="px-4 py-3 border border-gray-100 rounded-lg bg-white mb-2 text-sm flex justify-between">
                <div>
                  <span className="font-medium">Assessment #{a.id}</span>
                  <span className="ml-2 text-gray-500">{a.submitted_at ? new Date(a.submitted_at).toLocaleDateString() : ''}</span>
                </div>
                <div className="flex items-center gap-2">
                  {a.maturity_level && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-bold">{a.maturity_level}</span>}
                  <span className="text-sm font-semibold text-gray-700">{a.total_score?.toFixed(1)}%</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Submitted screen ───────────────────────────────────────────────────────
  if (step === 'submitted') {
    return (
      <div className="max-w-md mx-auto text-center py-16 space-y-4">
        <span className="text-6xl">✅</span>
        <h1 className="text-2xl font-bold text-gray-900">Assessment Submitted!</h1>
        <p className="text-gray-500">Your POSH assessment has been scored. View your results on the dashboard.</p>
        <button onClick={() => navigate('/app/dashboard')} className="px-6 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700">
          Go to Dashboard
        </button>
      </div>
    );
  }

  // ── Review screen ──────────────────────────────────────────────────────────
  if (step === 'review') {
    const totalResponses = Object.values(responses).length;
    const answered = Object.values(responses).filter(r => r.response_value).length;
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Review & Submit</h1>
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <p className="text-sm text-gray-600 mb-4">
            You have answered <strong>{answered}</strong> of <strong>{totalResponses}</strong> questions across {categories.length} categories.
          </p>
          {categories.map((cat, i) => (
            <button
              key={cat.id}
              onClick={() => { setStep('category'); loadCategoryQuestions(i); }}
              className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-gray-50 border border-gray-100 mb-2 flex justify-between"
            >
              <span>{cat.name}</span>
              <span className="text-blue-600 text-xs">Edit →</span>
            </button>
          ))}
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => { setStep('category'); loadCategoryQuestions(categories.length - 1); }}
            className="flex-1 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
          >
            ← Back
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 py-2.5 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:opacity-60"
          >
            {submitting ? 'Submitting…' : 'Submit Assessment'}
          </button>
        </div>
      </div>
    );
  }

  // ── Category question screen ────────────────────────────────────────────────
  const cat = categories[currentCatIndex];
  const progress = ((currentCatIndex) / categories.length) * 100;

  return (
    <div className="max-w-3xl mx-auto space-y-5">
      {/* Progress */}
      <div>
        <div className="flex justify-between text-xs text-gray-500 mb-1">
          <span>Step {currentCatIndex + 1} of {categories.length}</span>
          <span>{progress.toFixed(0)}% complete</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-1.5">
          <div className="h-1.5 bg-blue-600 rounded-full transition-all" style={{ width: `${progress}%` }} />
        </div>
      </div>

      <div>
        <h2 className="text-xl font-bold text-gray-900">{cat?.name}</h2>
        <p className="text-xs text-gray-500 mt-0.5">{cat?.description}</p>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-1 border-b border-gray-200">
        <button
          onClick={() => setCatTab('questions')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            catTab === 'questions'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Questions
        </button>
        <button
          onClick={() => {
            setCatTab('evidence');
            if (activeAssessment && cat) {
              loadCategoryEvidence(activeAssessment.id, cat.id);
            }
          }}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            catTab === 'evidence'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Upload Evidence
        </button>
      </div>

      {/* Questions tab */}
      {catTab === 'questions' && (
        <div className="space-y-4">
          {questions.map((q, qi) => {
            const resp = responses[q.id];
            return (
              <div key={q.id} className="bg-white border border-gray-200 rounded-xl p-5">
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="flex-1">
                    <span className={`text-xs px-2 py-0.5 rounded font-medium mr-2 ${
                      q.requirement_type === 'must_have'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {q.requirement_type === 'must_have' ? 'MUST HAVE' : 'GOOD TO HAVE'}
                    </span>
                    <p className="text-sm font-medium text-gray-800 mt-1">{q.question_text}</p>
                    {q.guidance_note && (
                      <p className="text-xs text-gray-500 mt-1 italic">{q.guidance_note}</p>
                    )}
                  </div>
                  <label className="flex items-center gap-1 text-xs text-gray-500 flex-shrink-0">
                    <input
                      type="checkbox"
                      checked={resp?.is_applicable}
                      onChange={(e) => handleResponse(q.id, 'is_applicable', e.target.checked)}
                      className="rounded"
                    />
                    Applicable
                  </label>
                </div>

                {resp?.is_applicable && (
                  <>
                    {q.question_type === 'yes_no' && (
                      <div className="flex gap-2">
                        {['yes', 'no'].map((val) => (
                          <button
                            key={val}
                            onClick={() => handleResponse(q.id, 'response_value', val)}
                            className={`flex-1 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                              resp?.response_value === val
                                ? val === 'yes'
                                  ? 'bg-green-600 text-white border-green-600'
                                  : 'bg-red-600 text-white border-red-600'
                                : 'border-gray-200 text-gray-600 hover:border-gray-400'
                            }`}
                          >
                            {val === 'yes' ? '✓ Yes' : '✗ No'}
                          </button>
                        ))}
                      </div>
                    )}
                    {q.question_type === 'scale' && (
                      <input
                        type="range"
                        min="0"
                        max={q.max_score}
                        step="0.5"
                        value={resp?.response_value || '0'}
                        onChange={(e) => handleResponse(q.id, 'response_value', e.target.value)}
                        className="w-full"
                      />
                    )}
                    <textarea
                      placeholder="Add comments (optional)…"
                      value={resp?.comments || ''}
                      onChange={(e) => handleResponse(q.id, 'comments', e.target.value)}
                      className="mt-2 w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 resize-none focus:outline-none focus:ring-1 focus:ring-blue-400"
                      rows={2}
                    />
                    {q.evidence_required && (
                      <p className="text-xs text-amber-600 mt-1">📎 Evidence required for this item</p>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Upload Evidence tab */}
      {catTab === 'evidence' && (
        <div className="space-y-4">
          {/* Upload zone */}
          <div className="bg-white border-2 border-dashed border-gray-200 rounded-xl p-8 text-center">
            <span className="text-4xl">📁</span>
            <p className="text-sm text-gray-600 mt-2 font-medium">
              Upload evidence for "{cat?.name}"
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Allowed: PDF, JPG, PNG, DOC, DOCX · Max {MAX_MB} MB
            </p>
            {uploadError && <p className="mt-2 text-sm text-red-600">{uploadError}</p>}
            {uploadSuccess && <p className="mt-2 text-sm text-green-600">{uploadSuccess}</p>}
            <label className="mt-4 inline-block cursor-pointer px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
              {uploading ? 'Uploading…' : 'Choose File'}
              <input
                ref={fileRef}
                type="file"
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={handleEvidenceUpload}
                disabled={uploading}
              />
            </label>
          </div>

          {/* Evidence list for this category */}
          {evidenceLoading ? (
            <div className="flex justify-center py-6">
              <div className="animate-spin h-8 w-8 border-b-2 border-blue-600 rounded-full" />
            </div>
          ) : catEvidence.length > 0 ? (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">File Name</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Type</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Size</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Uploaded</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500"></th>
                  </tr>
                </thead>
                <tbody>
                  {catEvidence.map((ev) => (
                    <tr key={ev.id} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-800">{ev.file_name}</td>
                      <td className="px-4 py-3 text-gray-500 uppercase text-xs">{ev.file_type}</td>
                      <td className="px-4 py-3 text-gray-500">
                        {ev.file_size ? `${(ev.file_size / 1024).toFixed(0)} KB` : '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-500">
                        {new Date(ev.uploaded_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                          ev.is_verified ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {ev.is_verified ? '✓ Verified' : 'Pending'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleEvidenceDelete(ev.id)}
                          className="text-xs text-red-500 hover:underline"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center text-gray-400 py-6 text-sm">
              No evidence uploaded for this category yet.
            </p>
          )}
        </div>
      )}

      {/* Navigation */}
      <div className="flex gap-3 pb-6">
        {currentCatIndex > 0 && (
          <button
            onClick={() => { setCatTab('questions'); setCatEvidence([]); loadCategoryQuestions(currentCatIndex - 1); }}
            className="flex-1 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
          >
            ← Previous
          </button>
        )}
        <button
          onClick={saveAndNext}
          disabled={saving}
          className="flex-1 py-2.5 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-60"
        >
          {saving ? 'Saving…' : currentCatIndex + 1 < categories.length ? 'Save & Next →' : 'Save & Review'}
        </button>
      </div>
    </div>
  );
};

export default AssessmentPage;
